import { Injectable } from '@angular/core';

type SupportedLanguage = 'en' | 'hi';

declare global {
  interface Window {
    google?: {
      translate?: {
        TranslateElement?: new (
          options: { pageLanguage: string; includedLanguages: string },
          elementId: string
        ) => unknown;
      };
    };
    googleTranslateElementInit?: () => void;
  }
}

@Injectable({
  providedIn: 'root'
})
export class LanguageService {
  private readonly storageKey = 'site_language';
  private readonly cookieName = 'googtrans';
  private readonly translateElementId = 'google_translate_element';
  private scriptLoadPromise: Promise<void> | null = null;
  private currentLanguage: SupportedLanguage = this.readEffectiveLanguage();
  /** Avoid stacking Google Translate refreshes during SPA navigation (breaks Angular-bound text). */
  private readonly refreshDebounceMs = 550;
  private scheduledRefreshHandle: ReturnType<typeof setTimeout> | null = null;

  initialize(): Promise<void> {
    this.cancelScheduledRefresh();
    this.syncFromBrowser();
    if (this.currentLanguage === 'en') {
      this.resetToEnglish();
      return Promise.resolve();
    }

    return this.ensureTranslateScript().then(() => this.hardRefreshHindiTranslation());
  }

  /**
   * Single source of truth: googtrans cookie (when present) wins, else localStorage.
   * Persists the resolved value back to localStorage so Http interceptors stay aligned.
   */
  syncFromBrowser(): SupportedLanguage {
    this.currentLanguage = this.readEffectiveLanguage();
    localStorage.setItem(this.storageKey, this.currentLanguage);
    return this.currentLanguage;
  }

  getCurrentLanguage(): SupportedLanguage {
    return this.currentLanguage;
  }

  setLanguage(language: SupportedLanguage): Promise<void> {
    this.cancelScheduledRefresh();
    this.currentLanguage = language;
    localStorage.setItem(this.storageKey, language);

    if (language === 'en') {
      this.resetToEnglish();
      return Promise.resolve();
    }

    return this.ensureTranslateScript().then(() => this.hardRefreshHindiTranslation());
  }

  /**
   * Google Translate widget does not reliably translate newly-rendered SPA DOM.
   * Call this after route navigation or after async data renders.
   */
  refresh(): Promise<void> {
    if (this.currentLanguage === 'en') {
      return Promise.resolve();
    }
    return this.ensureTranslateScript().then(() => this.hardRefreshHindiTranslation());
  }

  /**
   * Full reset cycle for Hindi: force widget to English (strip GT-injected nodes),
   * then apply Hindi again — closest to a fresh page load without reloading the SPA.
   */
  private hardRefreshPromise: Promise<void> | null = null;

  async hardRefreshHindiTranslation(): Promise<void> {
    if (this.currentLanguage !== 'hi') {
      return;
    }
    if (this.hardRefreshPromise) {
      return this.hardRefreshPromise;
    }
    this.hardRefreshPromise = (async () => {
      await this.ensureTranslateScript();
      await this.applyLanguage('en');
      await this.delay(240);
      await this.applyLanguage('hi');
    })();
    try {
      await this.hardRefreshPromise;
    } finally {
      this.hardRefreshPromise = null;
    }
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  /**
   * Debounced refresh so rapid navigations + navbar remount do not repeatedly
   * mutate the DOM while Angular is rendering (empty menu labels, wrong language).
   */
  scheduleRefresh(): void {
    if (this.currentLanguage === 'en') {
      return;
    }
    if (this.scheduledRefreshHandle) {
      clearTimeout(this.scheduledRefreshHandle);
    }
    this.scheduledRefreshHandle = setTimeout(() => {
      this.scheduledRefreshHandle = null;
      void this.refresh();
    }, this.refreshDebounceMs);
  }

  cancelScheduledRefresh(): void {
    if (this.scheduledRefreshHandle) {
      clearTimeout(this.scheduledRefreshHandle);
      this.scheduledRefreshHandle = null;
    }
  }

  private readEffectiveLanguage(): SupportedLanguage {
    const fromCookie = this.readGoogTransCookie();
    if (fromCookie) return fromCookie;

    const fromStorage = localStorage.getItem(this.storageKey);
    if (fromStorage === 'hi' || fromStorage === 'en') {
      return fromStorage;
    }
    return 'en';
  }

  private readGoogTransCookie(): SupportedLanguage | null {
    // googtrans cookie examples:
    // - /en/hi  => Hindi mode
    // - /en/en  => English mode
    const cookie = document.cookie || '';
    const match = cookie.match(/(?:^|;\s*)googtrans=([^;]+)/);
    if (!match) return null;
    const value = decodeURIComponent(match[1] || '').trim();
    if (value.endsWith('/hi')) return 'hi';
    if (value.endsWith('/en')) return 'en';
    return null;
  }

  private ensureTranslateScript(): Promise<void> {
    if (window.google?.translate?.TranslateElement) {
      this.ensureTranslateElement();
      return Promise.resolve();
    }

    if (this.scriptLoadPromise) {
      return this.scriptLoadPromise;
    }

    this.scriptLoadPromise = new Promise<void>((resolve, reject) => {
      window.googleTranslateElementInit = () => {
        this.ensureTranslateElement();
        resolve();
      };

      const script = document.createElement('script');
      script.src = 'https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit';
      script.async = true;
      script.onerror = () => reject(new Error('Failed to load Google Translate script.'));
      document.body.appendChild(script);
    });

    return this.scriptLoadPromise;
  }

  private ensureTranslateElement(): void {
    if (!window.google?.translate?.TranslateElement) {
      return;
    }

    const element = document.getElementById(this.translateElementId);
    if (!element || element.childElementCount > 0) {
      return;
    }

    new window.google.translate.TranslateElement(
      {
        pageLanguage: 'en',
        includedLanguages: 'en,hi'
      },
      this.translateElementId
    );
  }

  private applyLanguage(language: SupportedLanguage): Promise<void> {
    this.writeGoogTransCookie(language);
    this.syncDocumentLanguage(language);

    return new Promise<void>((resolve) => {
      let attempts = 0;
      const maxAttempts = 60;

      const apply = () => {
        attempts += 1;
        const combo = document.querySelector<HTMLSelectElement>('.goog-te-combo');
        if (!combo) {
          if (attempts < maxAttempts) {
            setTimeout(apply, 150);
            return;
          }
          resolve();
          return;
        }

        if (combo.value !== language) {
          combo.value = language;
          combo.dispatchEvent(new Event('change'));
          combo.dispatchEvent(new Event('change', { bubbles: true }));
        }
        resolve();
      };

      apply();
    });
  }

  private writeGoogTransCookie(language: SupportedLanguage): void {
    const value = language === 'hi' ? '/en/hi' : '/en/en';
    const cookieValue = `${this.cookieName}=${value}; path=/`;
    document.cookie = cookieValue;
    document.cookie = `${cookieValue}; domain=${window.location.hostname}`;
  }

  private clearGoogTransCookie(): void {
    const expires = 'Thu, 01 Jan 1970 00:00:00 GMT';
    const hostname = window.location.hostname;
    const domains = [hostname, `.${hostname}`];

    document.cookie = `${this.cookieName}=; expires=${expires}; path=/`;
    for (const domain of domains) {
      document.cookie = `${this.cookieName}=; expires=${expires}; path=/; domain=${domain}`;
    }
  }

  private resetToEnglish(): void {
    this.clearGoogTransCookie();
    // Explicit /en/en cookie so Google Translate and interceptors agree on English mode.
    this.writeGoogTransCookie('en');
    this.syncDocumentLanguage('en');

    const combo = document.querySelector<HTMLSelectElement>('.goog-te-combo');
    if (combo) {
      combo.value = 'en';
      combo.dispatchEvent(new Event('change'));
      combo.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  private syncDocumentLanguage(language: SupportedLanguage): void {
    document.documentElement.lang = language;

    if (language === 'en') {
      document.body.classList.remove('translated-ltr', 'translated-rtl');
    }
  }

}
